import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

// Async thunk to create a banner
export const createBanner = createAsyncThunk(
    'banner/createBanner',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/banner', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.banner;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create banner');
        }
    }
);

// Async thunk to update a banner
export const updateBanner = createAsyncThunk(
    'banner/updateBanner',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.patch(`/banner/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.banner;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update banner');
        }
    }
);

// Async thunk to delete a banner
export const deleteBanner = createAsyncThunk(
    'banner/deleteBanner',
    async (id, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(`/banner/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete banner');
        }
    }
);

// Async thunk to fetch carousel banners
export const fetchCarouselBanners = createAsyncThunk(
    'banner/fetchCarouselBanners',
    async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/banner', {
                params: { type: 'carousel', page, limit },
            });
            return response.data.banners;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch carousel banners');
        }
    }
);

// Async thunk to fetch active login banner
export const fetchLoginBanner = createAsyncThunk(
    'banner/fetchLoginBanner',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/banner/active-login');
            return response.data.banner;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch login banner');
        }
    }
);

const bannerSlice = createSlice({
    name: 'banner',
    initialState: {
        carouselBanners: [],
        loginBanner: null,
        loading: false,
        error: null,
        success: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = null;
        },
    },
    extraReducers: (builder) => {
        // Create banner
        builder
            .addCase(createBanner.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = null;
            })
            .addCase(createBanner.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.bannerType === 'login') {
                    state.loginBanner = action.payload;
                } else {
                    state.carouselBanners.push(action.payload);
                }
                state.success = 'Banner created successfully';
            })
            .addCase(createBanner.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update banner
            .addCase(updateBanner.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = null;
            })
            .addCase(updateBanner.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.bannerType === 'login') {
                    state.loginBanner = action.payload;
                } else {
                    state.carouselBanners = state.carouselBanners.map((banner) =>
                        banner._id === action.payload._id ? action.payload : banner
                    );
                }
                state.success = 'Banner updated successfully';
            })
            .addCase(updateBanner.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delete banner
            .addCase(deleteBanner.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = null;
            })
            .addCase(deleteBanner.fulfilled, (state, action) => {
                state.loading = false;
                state.carouselBanners = state.carouselBanners.filter(
                    (banner) => banner._id !== action.payload
                );
                if (state.loginBanner?._id === action.payload) {
                    state.loginBanner = null;
                }
                state.success = 'Banner deleted successfully';
            })
            .addCase(deleteBanner.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch carousel banners
            .addCase(fetchCarouselBanners.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCarouselBanners.fulfilled, (state, action) => {
                state.loading = false;
                state.carouselBanners = action.payload;
            })
            .addCase(fetchCarouselBanners.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch login banner
            .addCase(fetchLoginBanner.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLoginBanner.fulfilled, (state, action) => {
                state.loading = false;
                state.loginBanner = action.payload;
            })
            .addCase(fetchLoginBanner.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, clearSuccess } = bannerSlice.actions;
export default bannerSlice.reducer;